import {isLongAnimationFrameEntry} from './criticalWorkflows/painWindow/performanceMetrics';
import {waitForPainWindowDelivery} from './criticalWorkflows/painWindow/waitForPainWindowDelivery';
import type {recordRumDurationVital} from './datadogRum';
import {getInputActionElement} from './inputActionContext';
import {performanceObserverConfiguration} from './performanceObserverConfiguration';

type IncompleteReason =
  | 'pagehide'
  | 'freeze'
  | 'view-changed'
  | 'stopped'
  | 'delivery-timeout'
  | 'entries-dropped';

interface PainWindow {
  startTime: number;
  endTime: number;
  windowDurationMs: number;
  minStartIntervalMs: number;
  inputType: string;
  initiatingWorkflow: string;
  action?: string;
  observedEndTime: number;
  backgroundedAt?: number;
  measurementComplete: boolean;
  longAnimationFrameCount: number;
  totalBlockingDurationMs: number;
  maxBlockingDurationMs: number;
  substantialBlockingFrameCount: number;
  severeBlockingFrameCount: number;
  blocking200To350MsCount: number;
  blocking350To500MsCount: number;
  blocking500To750MsCount: number;
  blockingOver750MsCount: number;
  cutoffTimer?: ReturnType<typeof setTimeout>;
  cancelDelivery?: () => void;
}

export interface InteractionPainController {
  /**
   * Reports the active window as incomplete, including any data collected so far.
   * This also stops waiting for frame timings if the window has already ended.
   * Keeps input listeners active for future windows.
   * Exposed so RUM view changes can interrupt sampling before switching views,
   * preventing an unfinished observation from being reported in the new view.
   */
  interrupt: (reason: IncompleteReason) => void;
  stop: () => void;
}

/** Starts a window on real clicks or key presses in a visible tab; only one window runs at a time. */
export function observeRumInteractionPain(
  report: typeof recordRumDurationVital
): InteractionPainController | undefined {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return undefined;
  }
  if (
    typeof PerformanceObserver === 'undefined' ||
    !PerformanceObserver.supportedEntryTypes?.includes('long-animation-frame')
  ) {
    report('interaction_pain_window_sampling', {
      startTime: performance.timeOrigin + performance.now(),
      duration: 0,
      context: {
        outcome: 'unsupported',
        configuredWindowDurationMs:
          performanceObserverConfiguration.getState().windowInteraction * 1000,
      },
    });
    return undefined;
  }

  // Wait until the current window is reported before allowing another to start.
  const windows = new Set<PainWindow>();
  let observer: PerformanceObserver | undefined;
  let stopped = false;
  let lastStartTime = -Infinity;

  // Include a frame if it started within the window, even if its timing data arrives later.
  // Count its full blocking time, including any work after the planned end.
  const processEntries = (entries: PerformanceEntryList) => {
    for (const entry of entries) {
      if (
        !isLongAnimationFrameEntry(entry) ||
        !Number.isFinite(entry.startTime) ||
        !Number.isFinite(entry.duration) ||
        entry.duration < 0 ||
        !Number.isFinite(entry.blockingDuration) ||
        entry.blockingDuration < 0
      ) {
        continue;
      }
      for (const sample of windows) {
        if (
          entry.startTime < sample.startTime ||
          entry.startTime >= sample.endTime
        ) {
          continue;
        }
        sample.observedEndTime = Math.max(
          sample.observedEndTime,
          entry.startTime + entry.duration
        );
        const blocking = entry.blockingDuration;
        sample.longAnimationFrameCount++;
        sample.totalBlockingDurationMs += blocking;
        sample.maxBlockingDurationMs = Math.max(
          sample.maxBlockingDurationMs,
          blocking
        );
        if (blocking > 200) {
          sample.substantialBlockingFrameCount++;
        }
        if (blocking > 500) {
          sample.severeBlockingFrameCount++;
        }
        if (blocking > 750) {
          sample.blockingOver750MsCount++;
        } else if (blocking > 500) {
          sample.blocking500To750MsCount++;
        } else if (blocking > 350) {
          sample.blocking350To500MsCount++;
        } else if (blocking > 200) {
          sample.blocking200To350MsCount++;
        }
        break;
      }
    }
  };

  // Report starts and interruptions separately so queries can distinguish
  // completed measurements from attempts that produced incomplete or missing data.
  const sampling = (
    sample: PainWindow,
    outcome: 'started' | 'incomplete',
    reason?: IncompleteReason
  ) => {
    report('interaction_pain_window_sampling', {
      startTime: performance.timeOrigin + sample.startTime,
      duration:
        outcome === 'started'
          ? 0
          : Math.min(
              sample.windowDurationMs,
              performance.now() - sample.startTime
            ),
      context: {
        outcome,
        reason,
        action: sample.action,
        backgrounded: sample.backgroundedAt != null,
        measurementComplete: false,
        inputType: sample.inputType,
        initiatingWorkflow: sample.initiatingWorkflow,
        configuredWindowDurationMs: sample.windowDurationMs,
        minStartIntervalMs: sample.minStartIntervalMs,
      },
    });
  };

  const finish = (sample: PainWindow, reason?: IncompleteReason) => {
    if (!windows.has(sample)) {
      return;
    }
    if (document.visibilityState !== 'visible') {
      sample.backgroundedAt ??= performance.now();
    }
    processEntries(observer?.takeRecords() ?? []);
    windows.delete(sample);
    clearTimeout(sample.cutoffTimer);
    sample.cancelDelivery?.();
    if (windows.size === 0) {
      observer?.disconnect();
      observer = undefined;
    }
    if (!sample.measurementComplete && reason == null) {
      reason = 'entries-dropped';
    }
    if (reason != null) {
      sample.measurementComplete = false;
      sampling(sample, 'incomplete', reason);
    }
    const endTime =
      reason == null
        ? sample.endTime
        : Math.min(sample.endTime, performance.now());
    const observedEndTime = Math.max(endTime, sample.observedEndTime);
    report('interaction_pain_window', {
      startTime: performance.timeOrigin + sample.startTime,
      duration: observedEndTime - sample.startTime,
      context: {
        outcome: reason == null ? 'completed' : 'incomplete',
        reason,
        action: sample.action,
        backgrounded:
          sample.backgroundedAt != null &&
          sample.backgroundedAt <= observedEndTime,
        measurementComplete: sample.measurementComplete,
        configuredWindowDurationMs: sample.windowDurationMs,
        minStartIntervalMs: sample.minStartIntervalMs,
        inputType: sample.inputType,
        initiatingWorkflow: sample.initiatingWorkflow,
        longAnimationFrameCount: sample.longAnimationFrameCount,
        totalBlockingDurationMs: sample.totalBlockingDurationMs,
        maxBlockingDurationMs: sample.maxBlockingDurationMs,
        substantialBlockingFrameCount: sample.substantialBlockingFrameCount,
        severeBlockingFrameCount: sample.severeBlockingFrameCount,
        blocking200To350MsCount: sample.blocking200To350MsCount,
        blocking350To500MsCount: sample.blocking350To500MsCount,
        blocking500To750MsCount: sample.blocking500To750MsCount,
        blockingOver750MsCount: sample.blockingOver750MsCount,
      },
    });
  };

  const interrupt = (reason: IncompleteReason) => {
    for (const sample of windows) {
      finish(sample, reason);
    }
  };

  // Measure cooldown from the last successful start; changing RUM views does not reset it.
  // Config changes affect new windows. The running window keeps its original settings.
  const onInput = (event: Event) => {
    if (stopped || !event.isTrusted || document.visibilityState !== 'visible') {
      return;
    }
    const config = performanceObserverConfiguration.getState();
    const now = performance.now();
    if (
      !config.instrumentInteractions ||
      now - lastStartTime < config.minIntervalInteraction * 1000
    ) {
      return;
    }
    if (windows.size > 0) {
      return;
    }
    if (observer == null) {
      observer = new PerformanceObserver(
        (list, _observer, options?: {droppedEntriesCount?: number}) => {
          if (options?.droppedEntriesCount) {
            for (const sample of windows) {
              sample.measurementComplete = false;
            }
          }
          processEntries(list.getEntries());
        }
      );
      observer.observe({type: 'long-animation-frame', buffered: false});
    }
    const target = event.target;
    const initiatingWorkflow =
      target instanceof Element
        ? target
            .closest('[data-perf-action]')
            ?.getAttribute('data-perf-action') || 'unattributed'
        : 'unattributed';
    lastStartTime = now;
    const windowDurationMs = config.windowInteraction * 1000;
    const sample: PainWindow = {
      windowDurationMs,
      minStartIntervalMs: config.minIntervalInteraction * 1000,
      startTime: now,
      endTime: now + windowDurationMs,
      inputType: event.type,
      initiatingWorkflow,
      action:
        getInputActionElement(target)?.getAttribute('data-perf-action') ??
        undefined,
      observedEndTime: now,
      measurementComplete: true,
      longAnimationFrameCount: 0,
      totalBlockingDurationMs: 0,
      maxBlockingDurationMs: 0,
      substantialBlockingFrameCount: 0,
      severeBlockingFrameCount: 0,
      blocking200To350MsCount: 0,
      blocking350To500MsCount: 0,
      blocking500To750MsCount: 0,
      blockingOver750MsCount: 0,
    };
    windows.add(sample);
    sample.cutoffTimer = setTimeout(() => {
      sample.cancelDelivery = waitForPainWindowDelivery(deliveryComplete => {
        finish(sample, deliveryComplete ? undefined : 'delivery-timeout');
      });
    }, windowDurationMs);
    sampling(sample, 'started');
  };
  const onVisibility = () => {
    if (document.visibilityState !== 'visible') {
      for (const sample of windows) {
        sample.backgroundedAt ??= performance.now();
      }
    }
  };
  const abandon = (reason: 'pagehide' | 'freeze') => {
    for (const sample of windows) {
      sample.backgroundedAt ??= performance.now();
    }
    interrupt(reason);
  };
  const onPageHide = () => abandon('pagehide');
  const onFreeze = () => abandon('freeze');
  const listenerOptions = {capture: true, passive: true};
  window.addEventListener('click', onInput, listenerOptions);
  // Passive telemetry observes all trusted keys, including input fields; this is not a shortcut.
  // oxlint-disable-next-line wandb/no-ad-hoc-keydown-listener
  window.addEventListener('keydown', onInput, listenerOptions);
  document.addEventListener('visibilitychange', onVisibility, true);
  document.addEventListener('freeze', onFreeze, true);
  window.addEventListener('pagehide', onPageHide, true);
  return {
    interrupt,
    stop() {
      if (stopped) {
        return;
      }
      stopped = true;
      interrupt('stopped');
      window.removeEventListener('click', onInput, true);
      window.removeEventListener('keydown', onInput, true);
      document.removeEventListener('visibilitychange', onVisibility, true);
      document.removeEventListener('freeze', onFreeze, true);
      window.removeEventListener('pagehide', onPageHide, true);
    },
  };
}
